import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Camera, Keyboard, X } from "lucide-react";

export default function QRScanner({ onResult, label = "Scan QR / Badge" }) {
  const [mode, setMode] = useState("camera");
  const [manual, setManual] = useState("");
  const [err, setErr] = useState("");
  const scannerRef = useRef(null);
  const activeRef = useRef(false);
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;

  useEffect(() => {
    if (mode !== "camera") return undefined;
    const el = document.getElementById("qr-reader");
    if (!el) return undefined;
    const scanner = new Html5Qrcode("qr-reader");
    scannerRef.current = scanner;
    activeRef.current = true;
    const emit = (v) => onResultRef.current(v);
    const started = scanner
      .start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 230, height: 230 } },
        (decoded) => {
          if (!activeRef.current) return;
          activeRef.current = false;
          scanner.stop().then(() => emit(decoded)).catch(() => emit(decoded));
        },
        () => {}
      )
      .catch(() => setErr("Camera unavailable. Use manual entry below."));
    return () => {
      activeRef.current = false;
      started.finally(() => {
        if (scanner.isScanning) { scanner.stop().catch(() => {}); }
      });
    };
  }, [mode]);

  return (
    <div className="space-y-4" data-testid="qr-scanner">
      <div className="flex gap-2">
        <button
          data-testid="qr-mode-camera"
          onClick={() => { setErr(""); setMode("camera"); }}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition ${mode === "camera" ? "btn-gold" : "glass text-slate-300"}`}
        >
          <Camera size={16} /> Camera
        </button>
        <button
          data-testid="qr-mode-manual"
          onClick={() => setMode("manual")}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition ${mode === "manual" ? "btn-gold" : "glass text-slate-300"}`}
        >
          <Keyboard size={16} /> Manual
        </button>
      </div>

      {mode === "camera" ? (
        <div className="relative">
          <div className="rounded-2xl overflow-hidden gold-border scan-ring bg-black/40">
            <div id="qr-reader" style={{ width: "100%" }} />
          </div>
          {err && <p className="text-amber-400 text-sm mt-3 text-center">{err}</p>}
          <p className="text-center text-slate-400 text-xs mt-3">Point the camera at a {label.toLowerCase()} code</p>
        </div>
      ) : (
        <form
          onSubmit={(e) => { e.preventDefault(); if (manual.trim()) onResult(manual.trim()); }}
          className="space-y-3"
        >
          <input
            data-testid="qr-manual-input"
            value={manual}
            onChange={(e) => setManual(e.target.value)}
            placeholder="Enter code / badge / phone number"
            className="w-full bg-black/30 gold-border rounded-xl px-4 py-3 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
          />
          <button data-testid="qr-manual-submit" type="submit" className="w-full btn-gold py-3 rounded-xl">
            Continue
          </button>
        </form>
      )}
    </div>
  );
}
